import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card, Table, Tag, Typography, EmptyState, Input, Select, Space, Badge, Button, FilterResetButton, IconButton, Tooltip,
  theme, Segmented, StatCard, PageHeader, message,
} from '../../ui';
import { RoleBadge } from '../../components/RoleBadge';
import {
  Search, RefreshCw, Clock, AlertCircle, Plus, LayoutGrid, List, Download,
  Wrench, UserX, PauseCircle, ArrowUpCircle, CheckCircle2, Eye, Settings2,
} from 'lucide-react';
import { request } from '../../api/client';
import { useFocusRow } from '../../hooks/useFocusRow';
import { useServerSort } from '../../hooks/useServerSort';
import { usePermissionChecker, useAnyPermission, useAuthStore, useHasRole } from '../../store/auth';
import { useUrlTab } from '../../hooks/useUrlTab';
import { brand } from '../../assets/brand';
import { formatIstDate, formatIstTime } from '../../lib/time';
import { ComplaintFormModal } from './ComplaintFormModal';
import { ExportDialog } from '../../components/ExportDialog';
import { PALETTE } from '../../lib/palette';
import {
  ComplaintActionModal, actionsFor, STATUS_LABEL, STATUS_TONE, STATUS_MEANING,
  SEVERITY_LABEL, SEVERITY_TONE, TERMINAL_STATUSES, type ComplaintAction,
} from './complaintWorkflow';

/**
 * The complaints register.
 *
 * Two things changed here, and they are the same change: a complaint is no longer moved by picking a
 * new value from a dropdown.
 *
 * 1. STATUS IS NOT EDITABLE IN A CELL. Every move is a named act with its own dialog and its own
 *    mandatory remark — escalate says why, send back says what is missing, close says why it is over.
 *    The inline `<Select>` that used to sit in the Status column could close a complaint with one
 *    click and no explanation, which is precisely what nobody could audit afterwards.
 *
 * 2. OPEN WORK IS THE DEFAULT, AND THE SERVER DECIDES IT. The old page fetched everything and hid
 *    closed rows in React, so choosing any status filter silently un-hid them and the 500-row cap was
 *    being spent on an archive. `scope` now goes to the API.
 *
 * The board keeps drag-and-drop, but a drop OPENS the matching action dialog rather than firing the
 * move — you still get the fast gesture, and the remark is still collected.
 */

// Active (workflow) columns shown on the kanban. Terminal states live in the list view.
const COLUMNS: { key: string; title: string; color: string; tone: string }[] = [
  { key: 'OPEN', title: 'Open', color: 'var(--color-sky-500)', tone: '#E6F4FF' },
  { key: 'IN_PROGRESS', title: 'In Progress', color: 'var(--color-harvest-400)', tone: '#FFF7E0' },
  { key: 'AWAITING_INFO', title: 'Awaiting Info', color: PALETTE.plum[500], tone: '#F4ECFF' },
  { key: 'ESCALATED', title: 'Escalated', color: 'var(--color-danger-500)', tone: '#FFEDED' },
  { key: 'REOPENED', title: 'Reopened', color: PALETTE.plum[500], tone: '#F4ECFF' },
  { key: 'RESOLVED', title: 'Resolved', color: brand.primary, tone: '#EAF6EE' },
];

/**
 * Subtle per-status row background for the list view. A LOW-ALPHA tint of the status colour (not the
 * opaque light `tone`) so it's legible over BOTH the light and the dark row.
 */
const STATUS_ROW_TONE: Record<string, string> = {
  ...Object.fromEntries(COLUMNS.map((c) => [c.key, `${c.color}1f`])),
  CLOSED: 'transparent', REJECTED: 'transparent', CANCELLED: 'transparent',
};

const SEVERITY_OPTIONS = (['LOW', 'MED', 'HIGH', 'CRIT'] as const).map((v) => ({ value: v, label: SEVERITY_LABEL[v] }));
const STATUS_OPTIONS = [
  'OPEN', 'IN_PROGRESS', 'AWAITING_INFO', 'ESCALATED', 'REOPENED', 'RESOLVED', 'CLOSED', 'REJECTED', 'CANCELLED',
].map((v) => ({ value: v, label: STATUS_LABEL[v] }));

const SEV_ORDER: Record<string, number> = { LOW: 0, MED: 1, HIGH: 2, CRIT: 3 };
/** Most-urgent first: soonest SLA (a past date = breached) on top, no-SLA last. */
const bySla = (a: any, b: any) => {
  const t = (x: any) => (x.slaDueAt ? new Date(x.slaDueAt).getTime() : Infinity);
  return t(a) - t(b);
};

/** Which action a drop onto a board column corresponds to. A column with no action isn't droppable. */
const COLUMN_ACTION: Record<string, string> = {
  IN_PROGRESS: 'start',
  AWAITING_INFO: 'send_back',
  ESCALATED: 'escalate',
  RESOLVED: 'resolve',
};

export function ComplaintsBoardPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  // Server-side. 'open' (default) excludes closed / rejected / withdrawn.
  const [scope, setScope] = useState<'open' | 'all' | 'closed'>('open');
  const [kpi, setKpi] = useState<'' | 'unassigned' | 'breached'>('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [acting, setActing] = useState<{ action: ComplaintAction; row: any } | null>(null);
  // In the URL so opening a complaint and pressing Back returns to the view you were in.
  const [view, setView] = useUrlTab('view', ['list', 'board'] as const, 'list');

  const hasPerm = usePermissionChecker();
  // Who is looking — whoever resolved a complaint is refused Close by the server, so it is not offered.
  const myUserId = useAuthStore((s) => s.user?.id ?? null);
  const isSuperAdmin = useHasRole('SUPER_ADMIN');
  const closeViewer = { userId: myUserId, isSuperAdmin };
  const canManage = useAnyPermission(['COMPLAINT.ASSIGN', 'COMPLAINT.RESOLVE', 'COMPLAINT.ESCALATE', 'COMPLAINT.CLOSE']);
  const canCreate = hasPerm('COMPLAINT.CREATE');
  const canManageCategories = hasPerm('COMPLAINT.CATEGORY.MANAGE');
  // Server gate is COMPLAINT.EXPORT — deliberately NOT COMPLAINT.VIEW, which a retailer and a farmer
  // both hold to follow their own complaint. The register names every complainant and their location.
  const canExport = hasPerm('COMPLAINT.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);
  const { sortState, onSortChange, sort } = useServerSort();
  // Server-side paging. The board view needs the whole active set to group into columns, so it asks
  // for a single large page; the list view pages properly.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Every filter that narrows rows is a SERVER filter, so the 500-row cap applies to what you asked
  // for rather than to an unfiltered fetch the browser then whittles down.
  const params = useMemo(() => ({
    // `page`/`pageSize` are what switch the API to its `{ data, meta }` envelope; without them it
    // answers with a bare capped array for the Android client's sake.
    page: view === 'board' ? 1 : page,
    pageSize: view === 'board' ? 500 : pageSize,
    ...(sort ? { sort } : {}),
    ...(status ? { status } : { scope }),
    ...(severity ? { severity } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(kpi === 'unassigned' ? { unassigned: 'true' } : {}),
    ...(kpi === 'breached' ? { breached: 'true' } : {}),
  }), [sort, status, scope, severity, categoryId, search, kpi, page, pageSize, view]);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['complaints', params],
    queryFn: () => request<{ data: any[]; meta: { total: number } }>({ url: '/complaints', params }),
    // Keeps the previous page on screen while the next one loads, instead of flashing an empty table.
    placeholderData: (prev) => prev,
  });

  /*
   * Any filter change invalidates the page number — page 4 of "all complaints" is not page 4 of
   * "escalated only", and landing on an out-of-range page shows an empty table with rows that do
   * exist. Reset on every filter, never on a sort (which reorders the same set).
   */
  useEffect(() => { setPage(1); }, [status, scope, severity, categoryId, search, kpi]);
  /*
   * KPI counts come from their own endpoint, not from `data`. A tile counted off the loaded list
   * would silently be "of the 500 that fit", and clicking "Past SLA" would then make its own tile
   * read 0 because the list it counts is already filtered to it.
   */
  const { data: summary } = useQuery({
    queryKey: ['complaint-summary'],
    queryFn: () => request<any>({ url: '/complaints/summary' }),
  });
  const { data: categories } = useQuery({
    queryKey: ['complaint-categories'],
    queryFn: () => request<any[]>({ url: '/complaint-categories' }),
  });
  /*
   * The people a complaint may be handed to, ANSWERED BY THE SERVER.
   *
   * This used to read `GET /users?pageSize=200` and sieve it in the browser, which failed three ways
   * at once: that endpoint returns a bare ARRAY (so `.data` was undefined), its `role` is an OBJECT
   * (so the role test compared against "[object Object]"), and the request was gated on `USER.VIEW`
   * as well — so the dropdown said "No results" to everybody. The eligibility rule was also a second
   * copy of one the server already enforces, and it read the primary role only, which made a
   * multi-role Sales Head invisible.
   */
  const { data: assignableStaff } = useQuery({
    // Scoped to the row being acted on — the eligible people differ per complaint (the reps who
    // cover THAT dealer's territory, plus Admin/SA), so the list is fetched when a row is picked.
    queryKey: ['complaint-assignable', acting?.row?.id ?? null],
    queryFn: () => request<{ id: string; fullName: string; roleCode: string }[]>({
      url: '/complaints/assignable', params: acting?.row?.id ? { complaintId: acting.row.id } : {},
    }),
    enabled: hasPerm('COMPLAINT.ASSIGN') && !!acting?.row?.id,
  });
  /*
   * Name + the role as a COLOURED PILL, the house convention for a person anywhere in the app
   * (components/RoleBadge). This list is deliberately mixed — Super Admins, Admins and Sales Heads
   * together — and "· sales head" in grey lower-case made the reader parse text to tell a senior
   * from an administrator. `searchLabel` keeps type-to-search working over the plain words.
   */
  const assignees = useMemo(
    () => (assignableStaff ?? []).map((u) => ({
      value: String(u.id),
      searchLabel: `${u.fullName} ${String(u.roleCode ?? '').replace(/_/g, ' ')}`,
      label: (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{u.fullName}</span>
          <RoleBadge role={u.roleCode} />
        </span>
      ),
    })),
    [assignableStaff],
  );

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? rows.length;
  const activeRows = useMemo(() => rows.filter((c) => !TERMINAL_STATUSES.includes(c.status)), [rows]);
  const grouped = useMemo(
    () => activeRows.reduce<Record<string, any[]>>((acc, c) => {
      (acc[c.status] = acc[c.status] || []).push(c);
      return acc;
    }, {}),
    [activeRows],
  );

  const openAction = (row: any, key: string) => {
    const action = actionsFor(row, hasPerm, closeViewer).find((a) => a.key === key);
    if (!action) {
      message.warning(`You can't ${key.replace(/_/g, ' ')} a ${STATUS_LABEL[row.status] ?? row.status} complaint.`);
      return;
    }
    setActing({ action, row });
  };

  const onDrop = (e: React.DragEvent, col: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    setHoverCol(null);
    if (!id) return;
    const card = rows.find((c) => String(c.id) === String(id));
    if (!card || card.status === col) return;
    const key = COLUMN_ACTION[col];
    if (!key) { message.warning(`Drag a card onto a column you can move it to.`); return; }
    // The drop opens the dialog; it never performs the move on its own. The gesture picks the
    // action, the dialog still collects the remark that action requires.
    openAction(card, key);
  };

  const hasFilters = !!(search || severity || status || categoryId || kpi || scope !== 'open');
  const clearFilters = () => {
    setSearch(''); setSeverity(''); setStatus(''); setCategoryId(''); setKpi(''); setScope('open');
  };

  /*
   * `?focus=<id>` used to EXPAND the row a hub link or notification meant. With the strip gone there
   * is nothing to expand, so the link now opens that complaint outright — which is what the sender
   * meant by it in the first place.
   */
  const focus = useFocusRow(rows, {
    match: ['id', 'complaintNo'],
    onFocus: (r: any) => navigate(`/complaints/${r.id}`),
  });

  const slaCell = (v: string, r: any) => {
    if (!v) return '—';
    // A finished complaint has no live deadline; printing one invites someone to read it as late.
    if (TERMINAL_STATUSES.includes(r.status)) return <span className="text-muted">—</span>;
    if (r.status === 'AWAITING_INFO') {
      return <Tooltip label="Paused — waiting on the complainant"><span className="text-muted"><PauseCircle size={16} /> paused</span></Tooltip>;
    }
    /*
     * Date and time on two DELIBERATE lines, each unwrappable.
     *
     * As one run of text the value was too long for the column and the browser broke it wherever it
     * ran out of room — which put a lonely "PM" on the second line, reading as a stray word rather
     * than as part of the timestamp above it. Splitting it ourselves means the break lands between
     * two whole facts, the way the approvals queue prints the same kind of value.
     */
    return (
      <span className="block leading-tight" style={{ color: r.slaBreached ? PALETTE.danger[500] : undefined }}>
        <span className="flex items-center gap-1 whitespace-nowrap">
          {r.slaBreached && <AlertCircle size={14} className="shrink-0" />}
          {formatIstDate(v)}
        </span>
        <span className="block whitespace-nowrap type-caption text-muted">{formatIstTime(v)}</span>
      </span>
    );
  };

  /** Row actions: 32px icon-only, fixed rank order, View last. */
  const rowActions = (r: any) => {
    const avail = actionsFor(r, hasPerm, closeViewer);
    const RANK = ['start', 'respond', 'accept', 'resolve', 'close', 'send_back', 'escalate', 'assign', 'dispute', 'reject', 'withdraw'];
    const shown = [...avail].sort((a, b) => RANK.indexOf(a.key) - RANK.indexOf(b.key)).slice(0, 3);
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        {shown.map((a) => (
          <Tooltip key={a.key} label={a.label}>
            <IconButton
              diameter={32}
              aria-label={`${a.label} ${r.complaintNo}`}
              className={a.danger
                ? 'border border-line text-muted hover:border-danger-300 hover:bg-danger-50 hover:text-danger-700'
                : 'border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700'}
              onClick={() => openAction(r, a.key)}
            >
              {a.icon}
            </IconButton>
          </Tooltip>
        ))}
        <Tooltip label="Open full detail">
          <IconButton
            diameter={32}
            aria-label={`Open ${r.complaintNo}`}
            className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
            onClick={() => navigate(`/complaints/${r.id}`)}
          >
            <Eye size={16} />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  /*
   * Widths are set for READABILITY, not to fit the viewport. Eleven columns cannot be squeezed into
   * 1600px without truncating the ones that matter — a complainant reading "Demo Retailer / Plot 12,
   * MIDC Industrial…" tells you nothing. Every column declares a width, so the table takes fixed
   * layout at its natural width and the container (already `overflow-x-auto`, and drag-pannable)
   * scrolls sideways instead. Shrinking columns to avoid a scrollbar just moves the loss into the
   * cells.
   */
  const listColumns: any[] = [
    { title: 'No.', dataIndex: 'complaintNo', width: 152, render: (v: string) => <b>{v}</b>, sortable: true },
    { title: 'Severity', dataIndex: 'severity', width: 112, sortable: true,
      sorter: (a: any, b: any) => (SEV_ORDER[a.severity] ?? 0) - (SEV_ORDER[b.severity] ?? 0),
      render: (s: string) => <Tag tone={SEVERITY_TONE[s]}>{SEVERITY_LABEL[s] ?? s}</Tag> },
    { title: 'Category', dataIndex: 'category', width: 164, render: (v: string) => (v ? <Tag>{v}</Tag> : '—') },
    {
      title: 'Complainant', width: 210, ellipsis: true,
      render: (_: any, r: any) => (r.party ? (
        <div>
          <span style={{ fontWeight: 500 }}>{r.party.name}</span>{' '}
          <Tag tone={r.party.type === 'RETAILER' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>
            {r.party.type === 'RETAILER' ? 'Retailer' : 'Farmer'}
          </Tag>
          {r.party.location ? <div className="text-xs" style={{ color: 'var(--gb-text-muted)' }}>{r.party.location}</div> : null}
        </div>
      ) : <span className="text-muted">—</span>),
    },
    // "Who raised it" was previously only reachable by opening the complaint, even though it is the
    // first thing anyone triaging asks.
    { title: 'Raised by', dataIndex: 'raisedByName', width: 186, ellipsis: true,
      render: (v: string, r: any) => (v ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-content" title={v}>{v}</span>
          {/* The role as a coloured pill, not grey lowercase text under the name — a triage queue
              mixes retailers, field staff and admins, and the hue tells them apart at a glance. */}
          {r.raisedByRole && <span className="shrink-0"><RoleBadge role={r.raisedByRole} /></span>}
        </div>
      ) : <span className="text-subtle">—</span>) },
    { title: 'Description', dataIndex: 'description', width: 300, ellipsis: true },
    { title: 'With', dataIndex: 'assignedToName', width: 166, ellipsis: true,
      /* Badged like "Raised by" beside it: this column mixes reps, heads and admins, and which
         KIND of person holds a complaint is most of what the reader is scanning for. Bare names
         made the two columns look like different sorts of fact when they are the same one. */
      render: (v: string, r: any) => (v ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-content" title={v}>{v}</span>
          {r.assignedToRole && <span className="shrink-0"><RoleBadge role={r.assignedToRole} /></span>}
        </div>
      ) : <Tag tone="gold">Unassigned</Tag>) },
    { title: 'Status', dataIndex: 'status', width: 136, sortable: true,
      render: (s: string) => (
        <Tooltip label={STATUS_MEANING[s] ?? ''}>
          <Tag tone={STATUS_TONE[s]}>{STATUS_LABEL[s] ?? s}</Tag>
        </Tooltip>
      ) },
    { title: 'SLA', dataIndex: 'slaDueAt', width: 176, sortable: true,
      sorter: (a: any, b: any) => bySla(a, b), render: slaCell },
    { title: 'Raised', dataIndex: 'createdAt', width: 120, sortable: true, render: (v: string) => formatIstDate(v) },
    { title: '', key: 'actions', width: 148, render: (_: any, r: any) => rowActions(r) },
  ];

  return (
    <div>
      <PageHeader
        title="Complaints"
        subtitle="Open service work first. Every move is recorded against whoever made it."
        icon={<Wrench />}
        actions={
          <Space wrap>
            <Segmented
              value={view}
              onChange={(v) => setView(v as 'board' | 'list')}
              options={[
                { label: 'Board', value: 'board', icon: <LayoutGrid size={16} /> },
                { label: 'List', value: 'list', icon: <List size={16} /> },
              ]}
            />
            {canManageCategories && (
              <Tooltip label="Categories, evidence rules and SLA">
                <Button icon={<Settings2 size={16} />} onClick={() => navigate('/masters/complaint-categories')}>
                  Categories
                </Button>
              </Tooltip>
            )}
            {canExport && <Button icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>}
            {canCreate && (
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => setFormOpen(true)}>
                Raise Complaint
              </Button>
            )}
          </Space>
        }
      />

      {/* Server-counted tiles. Each one is a filter; clicking it narrows the list below. */}
      {canManage && summary && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <StatCard
            label="Open" tone="sky" icon={<Wrench />} value={summary.open}
            hint="Not closed, rejected or withdrawn"
            active={!kpi && !status && scope === 'open'}
            onClick={() => { setKpi(''); setStatus(''); setScope('open'); }}
          />
          <StatCard
            label="Unassigned" tone="warning" icon={<UserX />} value={summary.unassigned}
            hint={summary.unassigned > 0 ? 'Nobody owns these' : 'All owned'}
            active={kpi === 'unassigned'}
            onClick={() => { setKpi(kpi === 'unassigned' ? '' : 'unassigned'); setStatus(''); }}
          />
          <StatCard
            label="Past SLA" tone="danger" icon={<AlertCircle />} value={summary.breached}
            hint="Excludes any waiting on the customer"
            active={kpi === 'breached'}
            onClick={() => { setKpi(kpi === 'breached' ? '' : 'breached'); setStatus(''); }}
          />
          <StatCard
            /* Neutral, not a warning tone: the SLA clock is stopped and the ball is with the
               customer, so this tile is information, not a queue anyone is late on. */
            label="Awaiting info" tone="neutral" icon={<PauseCircle />} value={summary.awaitingInfo}
            hint="SLA paused — with the customer"
            active={status === 'AWAITING_INFO'}
            onClick={() => { setStatus(status === 'AWAITING_INFO' ? '' : 'AWAITING_INFO'); setKpi(''); }}
          />
          <StatCard
            label="Escalated" tone="danger" icon={<ArrowUpCircle />} value={summary.escalated}
            hint="With someone senior"
            active={status === 'ESCALATED'}
            onClick={() => { setStatus(status === 'ESCALATED' ? '' : 'ESCALATED'); setKpi(''); }}
          />
          <StatCard
            label="To close" tone="success" icon={<CheckCircle2 />} value={summary.awaitingClosure}
            hint="Resolved, awaiting sign-off"
            active={status === 'RESOLVED'}
            onClick={() => { setStatus(status === 'RESOLVED' ? '' : 'RESOLVED'); setKpi(''); }}
          />
        </div>
      )}

      {/* Filters in COLUMN order: search, severity, category, status, scope. */}
      <Card className="mb-4" density="compact">
        <Space wrap size={8}>
          <Input
            allowClear
            prefix={<Search size={16} />}
            placeholder="Search no, description, batch" aria-label="Search no, description, batch"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 250 }}
          />
          <Select
            style={{ width: 148 }} value={severity} onChange={setSeverity}
            options={[{ value: '', label: 'All severities' }, ...SEVERITY_OPTIONS]}
          />
          <Select
            showSearch optionFilterProp="label" style={{ width: 190 }}
            value={categoryId} onChange={(v) => setCategoryId(String(v))}
            options={[{ value: '', label: 'All categories' },
              ...(categories ?? []).map((c: any) => ({ value: String(c.id), label: c.name }))]}
          />
          <Select
            style={{ width: 160 }} value={status} onChange={(v) => { setStatus(String(v)); setKpi(''); }}
            options={[{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS]}
          />
          {/* The scope toggle is what actually decides whether finished complaints are fetched. */}
          <Segmented
            value={scope}
            onChange={(v) => { setScope(v as any); setStatus(''); }}
            options={[
              { label: 'Open', value: 'open' },
              { label: 'Finished', value: 'closed' },
              { label: 'All', value: 'all' },
            ]}
          />
          {hasFilters && <FilterResetButton size="md" onClick={clearFilters} />}
          <Tooltip label="Refresh">
            <Button aria-label="Refresh" icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()} />
          </Tooltip>
          <Typography.Text type="secondary" className="text-xs">
            {view === 'board'
              ? (canManage ? 'Drag a card onto a column to start that action.' : 'Status is managed by the support team.')
              : (canManage
                  ? 'Click a row to open it. Every action asks for a remark and is recorded against you.'
                  : 'Click a row to open it. Status is managed by the support team.')}
          </Typography.Text>
        </Space>
      </Card>

      {view === 'board' && (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6, marginBottom: 20, alignItems: 'flex-start' }}>
          {COLUMNS.map((col) => {
            const items = (grouped[col.key] ?? []).slice().sort(bySla);
            const isHover = hoverCol === col.key;
            const droppable = !!COLUMN_ACTION[col.key];
            return (
              <div
                key={col.key}
                onDragOver={(e) => { if (!droppable) return; e.preventDefault(); if (hoverCol !== col.key) setHoverCol(col.key); }}
                onDragLeave={() => setHoverCol((h) => (h === col.key ? null : h))}
                onDrop={(e) => onDrop(e, col.key)}
                style={{
                  flex: '1 1 0',
                  minWidth: 224,
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 320px)',
                  background: isHover ? col.tone : token.colorFillQuaternary,
                  border: `1px solid ${isHover ? col.color : token.colorBorderSecondary}`,
                  borderRadius: 10,
                  padding: 10,
                  transition: 'background 120ms ease, border-color 120ms ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 12px' }}>
                  <Space size={8}>
                    <span style={{ width: 9, height: 9, borderRadius: 5, background: col.color, display: 'inline-block' }} />
                    <b>{col.title}</b>
                  </Space>
                  <Badge count={items.length} showZero color={col.color} />
                </div>

                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, marginInline: -2, paddingInline: 2 }}>
                  {items.length === 0 ? (
                    <div style={{ paddingTop: 28 }}>
                      <EmptyState title={isHover ? 'Drop to start' : 'No items'} />
                    </div>
                  ) : (
                    items.map((c) => (
                      <div
                        key={c.id}
                        draggable={canManage}
                        onDragStart={canManage ? (e) => {
                          setDraggingId(c.id);
                          e.dataTransfer.setData('text/plain', String(c.id));
                          e.dataTransfer.effectAllowed = 'move';
                        } : undefined}
                        onDragEnd={() => { setDraggingId(null); setHoverCol(null); }}
                        onClick={() => navigate(`/complaints/${c.id}`)}
                        className="rounded-lg border border-line bg-surface"
                        style={{
                          marginBottom: 8,
                          cursor: canManage ? 'grab' : 'pointer',
                          opacity: draggingId === c.id ? 0.4 : 1,
                          borderLeft: `4px solid ${col.color}`,
                          padding: 10,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <b className="text-sm">{c.complaintNo}</b>
                          {c.slaBreached && (
                            <Tooltip label="Past SLA"><AlertCircle size={16} style={{ color: 'var(--color-danger-500)' }} /></Tooltip>
                          )}
                        </div>
                        <Space size={4} wrap style={{ marginTop: 6 }}>
                          <Tag tone={SEVERITY_TONE[c.severity]} style={{ marginRight: 0 }}>{SEVERITY_LABEL[c.severity] ?? c.severity}</Tag>
                          {c.category && <Tag style={{ marginRight: 0 }}>{c.category}</Tag>}
                        </Space>
                        {c.party?.name && (
                          <div className="text-xs" style={{ marginTop: 6, fontWeight: 500 }}>{c.party.name}</div>
                        )}
                        {c.description && (
                          <div className="text-xs" style={{ marginTop: 4, color: token.colorTextSecondary, lineHeight: 1.4 }}>
                            {(c.description ?? '').substring(0, 84)}{c.description.length > 84 ? '…' : ''}
                          </div>
                        )}
                        <div className="text-2xs" style={{ color: token.colorTextTertiary, marginTop: 8 }}>
                          {c.status === 'AWAITING_INFO'
                            ? <><PauseCircle size={16} /> SLA paused — with the complainant</>
                            : c.slaDueAt
                              ? <span style={{ color: c.slaBreached ? PALETTE.danger[500] : undefined }}>
                                  <Clock size={16} /> SLA {formatIstDate(c.slaDueAt)} {formatIstTime(c.slaDueAt)}
                                </span>
                              : null}
                        </div>
                        <div className="text-2xs" style={{ color: token.colorTextTertiary, marginTop: 2 }}>
                          {c.assignedToName ? `With ${c.assignedToName}` : 'Unassigned'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === 'list' && (
        <Card density="compact">
          <Table
            rowKey="id"
            size="small"
            loading={isFetching}
            dataSource={rows}
            sortState={sortState}
            onSortChange={onSortChange}
            /* Controlled, server-driven: `total` is the count of everything matching the filters,
               not of the rows in hand, so the pager reports the real size of the register. */
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t: number) => `${t} complaint${t === 1 ? '' : 's'}`,
              onChange: (p: number, ps: number) => { setPage(p); if (ps !== pageSize) { setPageSize(ps); setPage(1); } },
            }}
            locale={{ emptyText: <EmptyState title={scope === 'open' ? 'No open complaints' : 'No complaints match your filters'} /> }}
            columns={listColumns}
            /*
             * Clicking a row OPENS the complaint. There is no expanded strip any more: it was a
             * cramped second copy of the detail page that could never carry the history, the
             * attachments or the actions — the three things anyone opening a complaint is actually
             * after. One place to read a complaint, and the row is the way in.
             */
            onRow={(r: any) => ({
              onClick: () => navigate(`/complaints/${r.id}`),
              style: { background: STATUS_ROW_TONE[r.status], cursor: 'pointer' },
            })}
            rowClassName={focus.rowClassName}
          />
        </Card>
      )}

      {/* No period picker: the register is a to-do list of what is open right now, ordered oldest-open
          first — a date range would hide exactly the ageing complaints it exists to surface. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export complaints"
        description="Every complaint with who raised it, who it is with, who resolved and closed it, and whether it passed SLA."
        baseUrl="/complaints/export"
        baseParams={params as any}
        hidePeriod
      />
      <ComplaintFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      <ComplaintActionModal
        open={!!acting}
        action={acting?.action ?? null}
        complaint={acting?.row ?? null}
        assignees={assignees}
        onClose={() => setActing(null)}
      />
    </div>
  );
}
